import { HttpErrorResponse } from '@angular/common/http';

/** Header enviado à API (e lido pelo interceptor) para rotear erros de validação ao formulário certo. */
export const CONTEXT_HEADER = 'Context';

export enum ErrorResponseType {
  Generic = 0,
  Connection = 1,
  BadRequest = 4,
  Unauthorized = 5,
  InternalError = 6,
  Forbidden = 7,
  NotFound = 8,
}

/** Erros de validação no formato `{campo: ['mensagem', ...]}`. */
export type ValidationErrors = Record<string, string[]>;

/** Erro normalizado pelo `apiInterceptor`. É o que as Promises/Observables da API rejeitam. */
export class ErrorResponse {
  constructor(
    readonly httpErrorResponse: HttpErrorResponse,
    readonly type: ErrorResponseType,
    readonly message: string,
    /** `error.errors` da resposta; senão `error.message`; senão `null`. */
    readonly errors: ValidationErrors | string | null = null,
    /** Valor do header `Context` da requisição, se houver. */
    readonly context: string | null = null,
  ) {}
}

/** Converte um `HttpErrorResponse` no `ErrorResponse` (mesmo mapeamento de status do site antigo). */
export function toErrorResponse(
  error: HttpErrorResponse,
  context: string | null = null,
): ErrorResponse {
  const { status } = error;
  const make = (type: ErrorResponseType, message: string, withErrors = true) =>
    new ErrorResponse(error, type, message, withErrors ? extractErrors(error) : null, context);

  if (status === 0) {
    return make(
      ErrorResponseType.Connection,
      'Error connecting to server, check your internet connection.',
      false,
    );
  }
  if (status >= 400 && status <= 499) {
    switch (status) {
      case 401:
        return make(ErrorResponseType.Unauthorized, 'Unauthorized.');
      case 403:
        return make(ErrorResponseType.Forbidden, 'Forbidden resource.');
      case 404:
        return make(ErrorResponseType.NotFound, 'Resource not found.');
      case 405:
        return make(ErrorResponseType.Forbidden, 'Operation not allowed.');
      default:
        return make(ErrorResponseType.BadRequest, 'Bad request.');
    }
  }
  if (status >= 500 && status <= 599) {
    return make(
      ErrorResponseType.InternalError,
      'Internal error on service, please try again in a few minutes.',
    );
  }
  return make(ErrorResponseType.Generic, error.statusText, false);
}

function extractErrors(error: HttpErrorResponse): ValidationErrors | string | null {
  const body = error.error as {
    errors?: ValidationErrors;
    message?: ValidationErrors | string;
  } | null;
  if (body && typeof body === 'object') {
    if (body.errors) {
      return body.errors;
    }
    if (body.message) {
      return body.message;
    }
  }
  return null;
}
